'use client';
import { Link, Text } from '@magicschool/spellbook';
import { PageLoader } from 'components/PageLoader';
import NextLink from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { AuthContainer } from '@/components/_containers/AuthContainer';
import { storage } from '@/features/storage';
import { AUTO_JOIN_CODE_KEY, OUTPUT_HISTORY_ENTRY_POINT_KEY } from '@/util/constant';
import { setToken } from './actions';
import { LoginButtonClient } from './LoginButtonClient';

export const StudentLoginClient = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const joinCode = searchParams.get('joinCode');
  const roomId = searchParams.get('roomId');
  const token = searchParams.get('token');
  const entryPoint = searchParams.get('entry');
  const hasProcessedToken = useRef(false);

  useEffect(() => {
    // Store join code for use after SSO callback
    if (joinCode) storage.setItem(AUTO_JOIN_CODE_KEY, joinCode);
    if (!token) return;

    // Prevent double execution in React Strict Mode
    if (hasProcessedToken.current) return;
    hasProcessedToken.current = true;

    const doIt = async () => {
      // Clear the token from the URL
      router.replace(location.pathname);
      await setToken(token);

      router.push(
        joinCode && roomId
          ? `/lti/s/rooms/${roomId}/tools`
          : roomId && entryPoint === OUTPUT_HISTORY_ENTRY_POINT_KEY
            ? `/lti/s/rooms/${roomId}/history`
            : joinCode
              ? `/s/join?joinCode=${encodeURIComponent(joinCode)}`
              : '/s/rooms',
      );
    };

    doIt();
  }, [joinCode, roomId, router, token, entryPoint]);

  if (token) {
    return <PageLoader />;
  }

  return (
    <AuthContainer>
      <AuthContainer.Illustration
        className="h-20 sm:h-28 w-auto"
        src="/icons/icon-backpack-2.svg"
        alt=""
        width={140}
        height={140}
        priority
      />
      <AuthContainer.Content size="xs">
        <Text variant="display-1" element="h1" className="text-center">
          <FormattedMessage id="auth.student-sign-in" />
        </Text>
        <LoginButtonClient />
        <Text variant="body-2" className="text-center">
          <FormattedMessage
            id="auth.no-login-message"
            values={{
              Link: (chunks) => (
                <Link key="link" asChild>
                  <NextLink href={{ pathname: '/s/join', query: joinCode ? { joinCode } : {} }}>{chunks}</NextLink>
                </Link>
              ),
            }}
          />
        </Text>
      </AuthContainer.Content>
      <AuthContainer.Footer />
    </AuthContainer>
  );
};
